import { format } from 'date-fns';
import { ArrowUpDown, Calendar, Edit, Trash2 } from 'lucide-react';

import { ServiceIncident } from '@/types/service/serviceIncident';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { ColumnDef } from '@tanstack/react-table';

export type IncidentRow = ServiceIncident;

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'MMM d, yyyy h:mm a');
  } catch {
    return 'Invalid date';
  }
};

export const createIncidentColumns = (
  onEdit: (incident: ServiceIncident) => void,
  onDelete: (incident: ServiceIncident) => void,
): ColumnDef<IncidentRow>[] => [
  {
    accessorKey: 'client_short_name',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className='justify-start px-0'
        >
          Client / Environment
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    cell: ({ row }) => {
      const client = row.getValue('client_short_name') as string;
      const environment = row.original.environment;

      if (client === 'global' || environment === 'global') {
        return (
          <Badge variant='outline' className='bg-blue-600 text-white border-blue-600'>
            Global
          </Badge>
        );
      }

      return (
        <div className='space-y-1'>
          <div className='font-medium'>{client}</div>
          <div className='text-xs text-muted-foreground'>{environment}</div>
        </div>
      );
    },
    enableSorting: true,
    enableGlobalFilter: true,
  },
  {
    accessorKey: 'incident_type',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className='justify-start px-0'
        >
          Type
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    cell: ({ row }) => {
      const type = row.getValue('incident_type') as string;
      return (
        <Badge
          variant='outline'
          className={
            type === 'maintenance'
              ? 'text-yellow-700 border-yellow-400 bg-yellow-50'
              : 'text-red-700 border-red-400 bg-red-50'
          }
        >
          {type === 'maintenance' ? 'Maintenance' : 'Event'}
        </Badge>
      );
    },
    enableSorting: true,
    enableGlobalFilter: true,
  },
  {
    accessorKey: 'title',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className='justify-start px-0'
        >
          Title
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    cell: ({ row }) => {
      return <div className='max-w-md truncate'>{row.getValue('title')}</div>;
    },
    enableSorting: true,
    enableGlobalFilter: true,
  },
  {
    accessorKey: 'status',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className='justify-start px-0'
        >
          Status
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    cell: ({ row }) => {
      const status = row.getValue('status') as string;
      return (
        <Badge
          variant={status === 'ongoing' ? 'destructive' : 'outline'}
          className={
            status === 'resolved'
              ? 'bg-green-600 text-white border-green-600 hover:bg-green-600'
              : ''
          }
        >
          {status === 'ongoing' ? 'Ongoing' : 'Resolved'}
        </Badge>
      );
    },
    enableSorting: true,
    enableGlobalFilter: true,
  },
  {
    accessorKey: 'start_time',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className='justify-start px-0'
        >
          <Calendar className='mr-2 h-4 w-4' />
          Start Time
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    cell: ({ row }) => {
      return <div className='text-sm'>{formatDateTime(row.getValue('start_time'))}</div>;
    },
    enableSorting: true,
  },
  {
    accessorKey: 'end_time',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className='justify-start px-0'
        >
          <Calendar className='mr-2 h-4 w-4' />
          End Time
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    cell: ({ row }) => {
      const endTime = row.getValue('end_time') as string | null;
      return <div className='text-sm'>{formatDateTime(endTime)}</div>;
    },
    enableSorting: true,
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const incident = row.original;

      return (
        <div className='flex items-center gap-2'>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => onEdit(incident)}
            className='h-8 w-8 p-0'
          >
            <Edit className='h-4 w-4' />
          </Button>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => onDelete(incident)}
            className='h-8 w-8 p-0 text-destructive hover:text-destructive'
          >
            <Trash2 className='h-4 w-4' />
          </Button>
        </div>
      );
    },
  },
];
